import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageTitle from '../../FormElements/PageTitle';
import SpinnerImage from '../../../../assets/images/spinner.gif';
import { compressImageForVision } from '../../../utils/compressImageForVision';
import {
	dropZoneActiveClass,
	dropZoneBaseClass,
	dropZoneHintClass,
	dropZoneTitleClass,
	previewImageClass,
	removeImageButtonClass,
	reportPanelClass,
	reportPanelInnerClass
} from '../../BestBall/gradeMyDraftStyles';

const MAX_FILE_BYTES = 8 * 1024 * 1024;

function toBase64(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result);
		reader.onerror = () => reject(new Error('Could not read file'));
		reader.readAsDataURL(file);
	});
}

async function prepareImageForUpload(file) {
	const compressed = await compressImageForVision(file);
	return toBase64(compressed);
}

export default function GradeMyDraftPage({ grade_best_ball_draft_endpoint }) {
	const [selectedFile, setSelectedFile] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [loadingPhase, setLoadingPhase] = useState('');
	const [error, setError] = useState('');
	const [report, setReport] = useState(null);
	const [previewUrl, setPreviewUrl] = useState('');
	const [isDropZoneOver, setIsDropZoneOver] = useState(false);
	const fileInputRef = useRef(null);
	const dropZoneRef = useRef(null);

	useEffect(
		() => {
			if (!selectedFile) {
				setPreviewUrl('');
				return undefined;
			}

			const objectUrl = URL.createObjectURL(selectedFile);
			setPreviewUrl(objectUrl);

			return () => URL.revokeObjectURL(objectUrl);
		},
		[selectedFile]
	);

	const validateAndSetFile = (file) => {
		setError('');
		setReport(null);

		if (!file) {
			setSelectedFile(null);
			return;
		}

		if (!file.type.startsWith('image/')) {
			setError('Please upload an image file.');
			setSelectedFile(null);
			return;
		}

		if (file.size > MAX_FILE_BYTES) {
			setError('Image is too large. Please upload an image under 8MB.');
			setSelectedFile(null);
			return;
		}

		setSelectedFile(file);
	};

	const handleFileChange = (event) => {
		const file = event.target.files?.[0];
		validateAndSetFile(file);
	};

	const handleDragOver = (event) => {
		event.preventDefault();
		event.dataTransfer.dropEffect = 'copy';
		setIsDropZoneOver(true);
	};

	const handleDragLeave = (event) => {
		const related = event.relatedTarget;
		if (related && dropZoneRef.current?.contains(related)) {
			return;
		}
		setIsDropZoneOver(false);
	};

	const handleDrop = (event) => {
		event.preventDefault();
		setIsDropZoneOver(false);
		const file = event.dataTransfer.files?.[0];
		validateAndSetFile(file);
	};

	useEffect(() => {
		const handleDragEnd = () => setIsDropZoneOver(false);
		document.addEventListener('dragend', handleDragEnd);
		return () => document.removeEventListener('dragend', handleDragEnd);
	}, []);

	const handleRemoveImage = () => {
		setSelectedFile(null);
		setReport(null);
		setError('');
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const postGradeDraft = async (body) => {
		const csrfToken = document.querySelector('[name="csrf-token"]')?.content;
		const response = await fetch(grade_best_ball_draft_endpoint, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRF-Token': csrfToken
			},
			body: JSON.stringify(body)
		});

		let payload;
		try {
			payload = await response.json();
		} catch (_parseError) {
			const timedOut = response.status === 503 || response.status === 504;
			throw new Error(
				timedOut
					? 'Draft grading timed out. Please try again with a smaller screenshot.'
					: 'Could not grade draft right now.'
			);
		}

		if (!response.ok || !payload.success) {
			throw new Error(payload.error || 'Could not grade draft right now.');
		}

		return payload;
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setError('');
		setReport(null);

		if (!selectedFile) {
			setError('Upload a draft board image first.');
			return;
		}

		setIsLoading(true);
		try {
			const imageDataUrl = await prepareImageForUpload(selectedFile);

			setLoadingPhase('Reading your draft board...');
			const extractPayload = await postGradeDraft({
				image_data_url: imageDataUrl,
				filename: selectedFile.name
			});

			setLoadingPhase('Generating your draft report...');
			const gradePayload = await postGradeDraft({
				extraction: extractPayload.extraction,
				extraction_cost: extractPayload.cost_breakdown
			});

			setReport(gradePayload);
		} catch (submitError) {
			setError(submitError.message || 'Could not grade draft right now.');
		} finally {
			setIsLoading(false);
			setLoadingPhase('');
		}
	};

	const dropZoneClass = isDropZoneOver
		? `${dropZoneBaseClass} ${dropZoneActiveClass}`
		: dropZoneBaseClass;

	return (
		<div className="container p-4 pt-4 lg:py-14 md:px-20 text-gray-800 dark:text-dark-text">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<PageTitle title="Grade My Draft" />
				<Link
					to="/best-ball-zone"
					className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
				>
					← Best Ball Zone
				</Link>
			</div>
			<p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
				Upload your draft board screenshot to get a Best Ball tournament report.
			</p>

			<div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
				<form
					onSubmit={handleSubmit}
					className="lg:col-span-1 p-4 md:p-6 border border-gray-200 dark:border-dark-border rounded-lg bg-white dark:bg-dark-card h-fit"
				>
					<input
						ref={fileInputRef}
						type="file"
						accept="image/*"
						onChange={handleFileChange}
						className="hidden"
					/>
					{previewUrl ? (
						<div className="mt-2 relative w-full">
							<button
								type="button"
								onClick={handleRemoveImage}
								className={removeImageButtonClass}
								aria-label="Remove uploaded image"
								title="Remove image"
							>
								×
							</button>
							<img
								src={previewUrl}
								alt="Draft board preview"
								className={previewImageClass}
							/>
							{selectedFile ? (
								<p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
									{selectedFile.name}
								</p>
							) : null}
						</div>
					) : (
						<div
							ref={dropZoneRef}
							onDragEnter={handleDragOver}
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
							onClick={() => fileInputRef.current?.click()}
							className={dropZoneClass}
						>
							<p className={dropZoneTitleClass}>
								Drag and drop screenshot here
							</p>
							<p className={dropZoneHintClass}>
								or click to browse files
							</p>
						</div>
					)}

					<p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
						Accepted formats: PNG, JPG, WEBP. Max size: 8MB.
					</p>

					<button
						type="submit"
						disabled={isLoading || !selectedFile}
						className="mt-4 w-full inline-flex items-center justify-center rounded-md bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium disabled:bg-gray-400 dark:disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors"
					>
						{isLoading ? 'Grading draft...' : 'Grade my draft'}
					</button>

					{error ? (
						<p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>
					) : null}
				</form>

				<div className={reportPanelClass}>
					<div className={reportPanelInnerClass}>
						{isLoading ? (
							<div className="flex-1 flex flex-col items-center justify-center py-12">
								<img
									src={SpinnerImage}
									alt="Generating report"
									className="w-10 h-10 object-contain"
								/>
								<p className="text-sm text-gray-700 dark:text-gray-200 mt-4">
									{loadingPhase || 'Generating your draft report...'}
								</p>
								<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
									This usually takes under a minute.
								</p>
							</div>
						) : report ? (
							<div className="flex-1 overflow-y-auto pr-1">
								<h2 className="text-2xl font-semibold text-gray-900 dark:text-dark-text">
									Draft Grade: {report.grade}
								</h2>
								{report.summary ? (
									<p className="text-sm text-gray-700 dark:text-gray-200 mt-2">
										{report.summary}
									</p>
								) : null}
								{report.longform_report ? (
									<div className="mt-5 p-4 rounded-lg bg-gray-50 dark:bg-dark-bg border border-gray-200 dark:border-dark-border">
										<p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line leading-7">
											{report.longform_report}
										</p>
									</div>
								) : null}
							</div>
						) : (
							<div className="flex-1 flex items-center justify-center">
								<p className="text-sm text-gray-500 dark:text-gray-400">
									Your draft report will appear here.
								</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
